import { useMemo, useCallback } from 'react'

import { FormattedNumber } from 'react-intl'

import type { ButtonClickedData } from '@b-stock/fe-core/bstock-react'
import {
  AnalyticsEntityType,
  AnalyticsSource,
} from '@b-stock/fe-core/bstock-react'

import AppliedFilterSection from '@/components/listings/search/SearchFilters/shared/AppliedFilterSection'
import PercentageRangeFilter from '@/components/listings/search/SearchFilters/shared/NumericRangeFilter/PercentageRangeFilter'
import type {
  ListingFilter,
  FilterProps,
  AppliedProps,
} from '@/components/listings/search/SearchFilters/types'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

export const LANDED_COST_SEARCH_FILTER_SESSION_KEY = 'landed_cost_filter_active'

// API uses 0-1 range (0.5 = 50%), with "null" meaning "don't filter",
// UI uses 0-100 range, with "100%" treated as "don't filter"
const convertApiToUi = (apiValue: number | null): number => {
  return apiValue !== null ? Math.round(apiValue * 10000) / 100 : 100
}

const convertUiToApi = (uiValue: number): number | null => {
  return uiValue === 100 ? null : uiValue / 100
}

const LandedCostPercentageFilter: ListingFilter<number | null> = ({
  onChange,
  desktopScreen,
  value,
  onError,
}: FilterProps<number | null>) => {
  const uiValue = convertApiToUi(value)
  const { track } = useAnalytics()

  const handleChange = useCallback(
    (newUiValue: number) => {
      const apiValue = convertUiToApi(newUiValue)
      onChange(apiValue)
    },
    [onChange]
  )

  const handleApplied = useCallback(
    (uiVal: number, buttonType: 'slider' | 'top_range') => {
      const apiValue = convertUiToApi(uiVal)

      // Mark this session as having used the landed cost filter
      if (apiValue !== null) {
        sessionStorage.setItem(
          LANDED_COST_SEARCH_FILTER_SESSION_KEY,
          JSON.stringify({ value: apiValue })
        )
      } else {
        sessionStorage.removeItem(LANDED_COST_SEARCH_FILTER_SESSION_KEY)
      }

      track('landed_cost_filter_applied', {
        top_range: Math.round(uiVal),
        button_range: 0,
        button_type: buttonType,
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
      })
    },
    [track]
  )

  return (
    <PercentageRangeFilter
      selected={uiValue}
      onChange={handleChange}
      onError={onError}
      desktopScreen={desktopScreen}
      analyticsTitle="Landed Cost as % of MSRP"
      buttonName="landed_cost_percent_msrp"
      inputLabel="Filters.landedCostPercentage.label"
      onApplied={handleApplied}
    />
  )
}

LandedCostPercentageFilter.expandedOnInit = false
LandedCostPercentageFilter.isAlwaysDisplayed = true
LandedCostPercentageFilter.getOwnProps = ({ state, updateFilters }) => ({
  filterKey: 'landedCostPercentage',
  onChange: (landedCostPercentage: number | null) => {
    updateFilters({ landedCostPercentage })
  },
  value: state.filters.landedCostPercentage,
})

LandedCostPercentageFilter.label = 'Filters.landedCostPercentage'

LandedCostPercentageFilter.tooltip = 'Filters.landedCostPercentage.tooltip'

LandedCostPercentageFilter.getOnExpand = (
  trackButtonClicked: (data: ButtonClickedData) => void
) => {
  return () => {
    trackButtonClicked({
      buttonName: 'filters',
      filter_title: 'Landed Cost as % of MSRP',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
    })
  }
}

const LandedCostPercentageApplied = ({
  filtersState,
  updateFilters,
}: AppliedProps) => {
  const apiPercentage = filtersState.landedCostPercentage

  const value = useMemo(() => {
    if (apiPercentage === null) {
      return null
    }

    return {
      key: 'landedCostPercentage',
      name: (
        <FormattedNumber
          style="percent"
          value={apiPercentage}
          maximumFractionDigits={2}
        />
      ),
    }
  }, [apiPercentage])

  const onRemove = useCallback(() => {
    sessionStorage.removeItem(LANDED_COST_SEARCH_FILTER_SESSION_KEY)
    updateFilters({ landedCostPercentage: null })
  }, [updateFilters])

  if (!value) {
    return <></>
  }

  return (
    <AppliedFilterSection
      label={LandedCostPercentageFilter.label}
      values={[value]}
      onRemove={onRemove}
    />
  )
}

LandedCostPercentageApplied.displayName = 'LandedCostPercentageApplied'

LandedCostPercentageFilter.Applied = LandedCostPercentageApplied

export default LandedCostPercentageFilter
